import { parseMoney } from "@/lib/api";

/**
 * Regras da revisão de importação, fora da tela.
 *
 * O que decide se uma linha está pronta e o que a barra de resumo conta mora
 * aqui porque a tela precisa da mesma resposta em quatro lugares — o marcador da
 * linha, o filtro de pendências, o contador do topo e o bloqueio do botão de
 * confirmar. Espalhado, um deles ficaria para trás.
 */

/** Como o item da NF-e casou com o catálogo. */
export const VIA = {
  ean: {
    tone: "success",
    label: "casou por EAN",
    // O código de barras é o mesmo: não há o que conferir.
    hint: null,
  },
  apelido: {
    tone: "info",
    label: "vínculo salvo",
    hint: null,
  },
  nome: {
    tone: "warning",
    label: "palpite por nome",
    // Trigrama acima de 0.35 acerta bastante e erra o suficiente para não se
    // confiar de olhos fechados — "Cadeado LT-20MM" e "Cadeado LT-25MM" são
    // quase o mesmo texto.
    hint: "Parecido no nome, não idêntico. Confira antes de confirmar.",
  },
};

/** O que falta nesta linha para ela poder ser importada. */
export function pendencia(r) {
  if (r.action === "skip") return null;
  if (r.action === "link" && !r.variant_id) return "Escolha o produto de destino";
  if (r.action === "create" && !r.category_slug) return "Escolha a categoria";
  if (r.action === "create" && !(parseMoney(r.price) > 0)) return "Informe o preço de venda";
  if (!(parseInt(r.qty, 10) > 0)) return "Quantidade precisa ser maior que zero";
  // Margem mínima acima da máxima é contradição, não preferência: o piso ficaria
  // acima do preço de etiqueta e o produto nasceria invendável. Bloqueia em vez
  // de corrigir sozinho — quem digitou tem de saber que estava invertido.
  if (margensInvertidas(r)) return "Margem mínima não pode passar da máxima";

  return null;
}

/** As duas margens do item estão invertidas? */
export function margensInvertidas(r) {
  const min = parseMoney(r.margin_min_percentage);
  const max = parseMoney(r.margin_max_percentage);

  return r.margin_min_percentage !== "" && r.margin_max_percentage !== "" && min > max;
}

/**
 * Linha que o operador ainda precisa olhar.
 *
 * Inclui o palpite por nome mesmo quando ele está "completo": tecnicamente dá
 * para confirmar, mas foi o computador que escolheu, e confirmar sem ler é como
 * o estoque de uma loja vai parar no produto errado.
 */
export function precisaAtencao(r) {
  return Boolean(pendencia(r)) || (r.action === "link" && r.via === "nome" && !r.revisado);
}

/** Contagens e totais que a barra do topo mostra. */
export function resumo(rows) {
  const conta = (acao) => rows.filter((r) => r.action === acao).length;
  // Custo REAL: já com frete, seguro, despesas, IPI e ST rateados pelo
  // servidor. Somar `unit_value` daria o valor de tabela do fornecedor, que é
  // sempre menor que o que a loja pagou de verdade.
  //
  // Usa o total EXATO da linha (`landed_total`) enquanto a quantidade for a da
  // nota. Multiplicar o custo unitário arredondado pela quantidade acumula
  // centavos — numa nota de três itens já dava R$ 0,05 de diferença, e esse
  // número aparece ao lado do total da NF-e, onde qualquer descolamento parece
  // erro de cálculo. Com a quantidade editada não há total exato a usar, e aí o
  // unitário é o melhor disponível.
  const custo = rows
    .filter((r) => r.action !== "skip")
    .reduce((s, r) => {
      const qty = parseInt(r.qty, 10) || 0;
      const original = r.landed_total != null && qty === r.qty_xml;

      return s + (original ? Number(r.landed_total) : (Number(r.unit_cost ?? r.unit_value) || 0) * qty);
    }, 0);

  return {
    vincular: conta("link"),
    criar: conta("create"),
    ignorar: conta("skip"),
    pecas: rows.filter((r) => r.action !== "skip").reduce((s, r) => s + (parseInt(r.qty, 10) || 0), 0),
    custo: Math.round(custo * 100) / 100,
    pendentes: rows.filter((r) => precisaAtencao(r)).length,
    bloqueadas: rows.filter((r) => pendencia(r)).length,
  };
}

/**
 * Preço de venda sugerido a partir do custo da nota.
 *
 * O valor unitário da NF-e é o que a loja PAGOU. A tela usava esse número como
 * preço de venda, então todo produto importado nascia com margem zero até
 * alguém redigitar preço a preço. Aqui ele vira sugestão com margem — o
 * operador ajusta o que for exceção, em vez de digitar tudo.
 *
 * Arredonda para os `,90` que a loja usa na etiqueta: 47,52 vira 47,90.
 */
export function precoSugerido(custo, margemPct) {
  const base = (Number(custo) || 0) * (1 + (Number(margemPct) || 0) / 100);
  if (!(base > 0)) return 0;
  const inteiro = Math.floor(base);
  const sugerido = base - inteiro > 0.9 ? inteiro + 1.9 : inteiro + 0.9;

  return Math.round(sugerido * 100) / 100;
}
